import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon,
  toGridSortField, toODataSortField, getODataRows,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import { useC360List, C360Table, ColumnChooser, ViewIcon, EditIcon, DeleteIcon } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { DERIVATION_API, FAMILY_TONE, unwrap } from './documentDerivationShared';

// BELGE TÜRETME TANIMLARI — LİSTE (menü 9561).
//
// Belge tipi adları KODDA SABİT DEĞİLDİR: /DocumentTypes ucundan gelir (backend'deki kayıt
// defteri). Yeni bir belge tipi kayıt defterine eklendiğinde bu dosyaya dokunulmaz — kolon
// etiketleri ve tip filtreleri kendiliğinden güncellenir.

const SEARCH_FIELDS = ['Code', 'Description'];
const VENDOR_API = '/api/vendors';

const ACTIVE_OPTIONS = [
  { value: 'true', label: 'Aktif' },
  { value: 'false', label: 'Pasif' },
];

export function DocumentDerivationsPage() {
  usePageMeta(
    'Belge Türetme Tanımları',
    'Bir belge kaydedildiğinde tanıma uyuyorsa sistem karşılık gelen ikinci belgeyi otomatik türetir.',
  );
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [catalog, setCatalog] = useState({ types: [], families: [] });
  const [vendorLabels, setVendorLabels] = useState({});

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  // Kayıt defteri — kolon etiketleri ve tip/aile filtreleri bundan beslenir.
  useEffect(() => {
    fetch(`${DERIVATION_API}/DocumentTypes`, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => { const d = unwrap(body); if (d) setCatalog(d); })
      .catch(() => {});
  }, []);

  const typeLabels = useMemo(
    () => Object.fromEntries((catalog.types || []).map((t) => [t.id, t.name])),
    [catalog],
  );
  const familyLabels = useMemo(
    () => Object.fromEntries((catalog.families || []).map((f) => [f.key, f.label])),
    [catalog],
  );

  const columnFilterMeta = useMemo(() => ({
    code: { type: 'text', odataField: 'Code' },
    description: { type: 'text', odataField: 'Description' },
    family: {
      type: 'select',
      odataField: 'Family',
      options: (catalog.families || []).map((f) => ({ value: f.key, label: f.label })),
    },
    sourceDocumentTypeId: {
      type: 'select',
      odataField: 'SourceDocumentTypeId',
      options: (catalog.types || []).map((t) => ({ value: t.id, label: t.name })),
    },
    targetDocumentTypeId: {
      type: 'select',
      odataField: 'TargetDocumentTypeId',
      options: (catalog.types || []).map((t) => ({ value: t.id, label: t.name })),
    },
    // Bool alanda tırnak istenmez → valueFormat: 'raw' ($filter=IsActive eq true).
    isActive: { type: 'select', odataField: 'IsActive', valueFormat: 'raw', options: ACTIVE_OPTIONS },
  }), [catalog]);

  const list = useC360List({
    apiBase: DERIVATION_API,
    columnFilterMeta,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'Code',
    defaultSortDir: 'asc',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  // Distribütör adları: liste yalnız Id taşır. Sayfadaki Id'ler TEK sorguda çözülür (satır
  // başına istek N+1 olurdu) ve sonuç birikimli cache'lenir.
  useEffect(() => {
    const ids = [...new Set(rows.flatMap((r) => [r.sourceVendorId, r.targetVendorId]).filter(Boolean))]
      .filter((id) => !vendorLabels[id]);
    if (ids.length === 0) return;
    const filter = ids.map((id) => `Id eq ${id}`).join(' or ');
    fetch(`${VENDOR_API}?$top=200&$filter=${encodeURIComponent(filter)}`, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        const map = {};
        getODataRows(data).forEach((v) => {
          const id = v.id || v.Id;
          const code = v.textId || v.TextId || '';
          const name = v.name || v.Name || '';
          map[id] = code ? `${code} - ${name}` : name || id;
        });
        setVendorLabels((c) => ({ ...c, ...map }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const vendorOf = (id) => (id ? vendorLabels[id] || '…' : '—');

  const openNew = () => navigate('/document-derivations/new');
  const openDetail = (row) => { if (row) navigate(`/document-derivations/${row.id}`, { state: { row } }); };
  // #282: kalem düzenleme moduna açar; göz/çift-tık izleme modunda açar.
  const openEditRow = (row) => { if (row) navigate(`/document-derivations/${row.id}?edit=1`, { state: { row } }); };

  /** Aktif/pasif. Pasif tanım türetme motoru tarafından hiç ele alınmaz. */
  const handleToggle = useCallback(async (row) => {
    try {
      const res = await fetch(`${DERIVATION_API}/Toggle`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: row.id, isActive: !row.isActive }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Durum değiştirilemedi');
      showToast('success', row.isActive ? 'Tanım pasifleştirildi' : 'Tanım aktifleştirildi');
      fetchData();
    } catch (err) {
      showToast('error', err.message || 'Durum değiştirilemedi');
    }
  }, [fetchData]);

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${DERIVATION_API}/Delete`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Tanım silinemedi');
      showToast('success', 'Tanım silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (err) {
      showToast('error', err.message || 'Tanım silinemedi');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('code', {
      header: 'Kod', meta: { filterType: 'text' },
      cell: (info) => (
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{info.getValue() || '-'}</span>
      ),
    }),
    columnHelper.accessor('description', {
      header: 'Açıklama', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('family', {
      header: 'Aile', meta: { filterType: 'select' },
      cell: (info) => (
        <StatusBadge tone={FAMILY_TONE[info.getValue()] || 'neutral'}>
          {familyLabels[info.getValue()] || info.getValue() || '-'}
        </StatusBadge>
      ),
    }),
    columnHelper.accessor('sourceDocumentTypeId', {
      header: 'Kaynak Belge Tipi', meta: { filterType: 'select' },
      cell: (info) => typeLabels[info.getValue()] || info.getValue() || '-',
    }),
    columnHelper.display({
      id: 'arrow', header: '', enableColumnFilter: false, enableHiding: false,
      cell: () => <span style={{ color: 'var(--brand-blue-1, #0073ea)', fontWeight: 700 }}>→</span>,
    }),
    columnHelper.accessor('targetDocumentTypeId', {
      header: 'Hedef Belge Tipi', meta: { filterType: 'select' },
      cell: (info) => typeLabels[info.getValue()] || info.getValue() || '-',
    }),
    columnHelper.display({
      id: 'sourceScope', header: 'Kaynak (Distribütör / Kriter)', enableColumnFilter: false,
      cell: ({ row }) => {
        const r = row.original;
        // Kriter boşsa "distribütörün tüm müşterileri" kapsanır — kural budur, eksik veri değil.
        const criteria = r.family === 'stok'
          ? (r.sourceWarehouseId ? 'Depo kriterli' : 'Tüm depolar')
          : (r.sourceCustomerCriteriaType || r.sourceCustomerId ? 'Müşteri kriterli' : 'Tüm müşteriler');
        return (
          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.35 }}>
            <b style={{ fontSize: 12.5 }}>{vendorOf(r.sourceVendorId)}</b>
            <small style={{ color: 'var(--text-secondary)' }}>{criteria}</small>
          </span>
        );
      },
    }),
    columnHelper.display({
      id: 'targetScope', header: 'Hedef (Distribütör / Müşteri)', enableColumnFilter: false,
      cell: ({ row }) => {
        const r = row.original;
        return (
          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.35 }}>
            <b style={{ fontSize: 12.5 }}>{vendorOf(r.targetVendorId)}</b>
            <small style={{ color: 'var(--text-secondary)' }}>
              {r.family === 'stok' ? '—' : (r.targetCustomerId ? 'Müşteri tanımlı' : 'Kaynaktan')}
            </small>
          </span>
        );
      },
    }),
    columnHelper.accessor('isActive', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => (
        <StatusBadge tone={info.getValue() ? 'ok' : 'neutral'}>
          {info.getValue() ? 'Aktif' : 'Pasif'}
        </StatusBadge>
      ),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && (
            <button
              type="button"
              className="c360-action-btn c360-action-btn--icon"
              title={row.original.isActive ? 'Pasifleştir' : 'Aktifleştir'}
              aria-label={row.original.isActive ? 'Pasifleştir' : 'Aktifleştir'}
              onClick={(e) => { e.stopPropagation(); handleToggle(row.original); }}
            >
              {row.original.isActive ? '⏸' : '⏵'}
            </button>
          )}
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete, typeLabels, familyLabels, vendorLabels, handleToggle]);

  const sorting = useMemo(
    () => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []),
    [sortField, sortDir],
  );
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('Code', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => row.id,
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'code', headerName: 'Kod' },
        { field: 'description', headerName: 'Açıklama' },
        { headerName: 'Aile', valueGetter: ({ row }) => familyLabels[row.family] || row.family || '' },
        { headerName: 'Kaynak Belge Tipi', valueGetter: ({ row }) => typeLabels[row.sourceDocumentTypeId] || row.sourceDocumentTypeId || '' },
        { headerName: 'Hedef Belge Tipi', valueGetter: ({ row }) => typeLabels[row.targetDocumentTypeId] || row.targetDocumentTypeId || '' },
        { headerName: 'Kaynak Distribütör', valueGetter: ({ row }) => vendorOf(row.sourceVendorId) },
        { headerName: 'Hedef Distribütör', valueGetter: ({ row }) => vendorOf(row.targetVendorId) },
        { headerName: 'Durum', valueGetter: ({ row }) => (row.isActive ? 'Aktif' : 'Pasif') },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `belge_turetme_tanimlari_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kod veya açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button
          type="button"
          style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          title="Excel'e Aktar"
          aria-label="Excel'e Aktar"
          onClick={handleExport}
        >
          <ExcelIcon size={16} />
        </button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Belge Türetme Tanımları"
        subtitle="Bir belge kaydedildiğinde, tanıma uyuyorsa sistem karşılık gelen ikinci belgeyi otomatik türetir. Türetme yalnızca aynı belge ailesi içinde yapılabilir."
        actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Tanım</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={columnFilterMeta}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Henüz türetme tanımı yapılmamış."
        />
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Tanımı Sil</h3>
            </div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>“{selected?.description}”</strong> tanımı silinecek ve bundan sonra hiçbir belge türetmeyecek.
                <br /><br />
                Daha önce <strong>türetilmiş belgeler silinmez</strong>; iz kayıtları da korunur.
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default DocumentDerivationsPage;
